import ubigeosData from "./ubigeos.json";

/**
 * Ubigeos de Perú (departamentos, provincias y distritos) incluidos en el
 * bundle. Los datos viven en `ubigeos.json` (copia local normalizada del
 * dataset de e-api) y se importan como módulo: no hay ninguna llamada HTTP en
 * tiempo de ejecución.
 *
 * Jerarquía: departamento (2 dígitos) -> provincia (4) -> distrito (6). El
 * código se toma del campo `ubigeo` del origen.
 */
export interface UbigeoDistrict {
  code: string;
  name: string;
}

export interface UbigeoProvince {
  code: string;
  name: string;
  districts: UbigeoDistrict[];
}

export interface UbigeoDepartment {
  code: string;
  name: string;
  provinces: UbigeoProvince[];
}

export interface UbigeoValue {
  department: string;
  province: string;
  district: string;
}

export const ubigeoDepartments: UbigeoDepartment[] = (ubigeosData as { departments: UbigeoDepartment[] })
  .departments;

const departmentByName = new Map<string, UbigeoDepartment>();
for (const department of ubigeoDepartments) {
  departmentByName.set(department.name, department);
}

export function getDepartment(name: string): UbigeoDepartment | undefined {
  if (!name) return undefined;
  return departmentByName.get(name);
}

export function getProvinces(departmentName: string): UbigeoProvince[] {
  return getDepartment(departmentName)?.provinces ?? [];
}

export function getDistricts(departmentName: string, provinceName: string): UbigeoDistrict[] {
  if (!provinceName) return [];
  const province = getProvinces(departmentName).find((item) => item.name === provinceName);
  return province?.districts ?? [];
}

export function emptyUbigeoValue(): UbigeoValue {
  return { department: "", province: "", district: "" };
}

export interface UbigeoCodes {
  department: string | null;
  province: string | null;
  district: string | null;
}

/** Devuelve los códigos de ubigeo de una selección (o null si no existe). */
export function findUbigeoCodes(value: UbigeoValue): UbigeoCodes {
  const department = getDepartment(value.department);
  if (!department) return { department: null, province: null, district: null };
  const province = department.provinces.find((item) => item.name === value.province);
  const district = province?.districts.find((item) => item.name === value.district);
  return {
    department: department.code,
    province: province?.code ?? null,
    district: district?.code ?? null,
  };
}
